import { defineMessages } from './define'

/* The lab screen (patient booking, the desk's rate card and the administrator's
 * inventory) under "lab.", and the account screens - notifications, profile,
 * account, change password and app settings - under "account.". */

export default defineMessages({
  en: {
    'lab.testsBooked': 'Tests booked',
    'lab.add': 'Add',
    'lab.inBasket': 'Added',
    'lab.selectedOne': '1 test selected',
    'lab.selectedN': '{n} tests selected',
    'lab.payable': '(Payable)',
    'lab.chooseTime': 'Choose collection time',
    'lab.bookNTitle': 'Book {n} tests',
    'lab.manyCashNote': 'Several tests are paid together at the desk. To pay by UPI, book one test at a time.',
    'lab.bookedManyFor': '{n} tests booked for {patient}.',
    'lab.bookedSome': 'Booked {done}, then: {error}',
    // ---------- lab ----------
    'lab.payQr': 'Scan QR code',
    'lab.uncategorised': 'Uncategorised',
    'lab.whenAt': '{date} at {time}',
    'lab.couldNotLoad': 'Could not load the inventory.',
    'lab.updated': '{name} updated.',
    'lab.added': '{name} added to the inventory.',
    'lab.couldNotSave': 'Could not save.',
    'lab.retired': '{name} retired.',
    'lab.couldNotRetire': 'Could not retire it.',
    'lab.timePassed': 'That time has passed. Pick a later one.',
    'lab.bookedFor': '{test} booked for {patient}.',
    'lab.bookedPaid': '{test} booked and paid.',
    'lab.bookedPaidReceipt': '{test} booked and paid — receipt {receipt}.',
    'lab.couldNotBook': 'Could not book the test.',
    'lab.addAnItem': 'Add an item',
    'lab.title': 'Lab inventory',
    'lab.reload': 'Reload',
    'lab.searchLabel': 'Search the rate card',
    'lab.searchPh': 'Name, lab, prep or rate — try 250 or <500',
    'lab.clear': 'Clear',
    'lab.kpiItems': 'Items',
    'lab.kpiPriced': 'Priced',
    'lab.kpiNoRate': 'No rate',
    'lab.kpiMedian': 'Median',
    'lab.kpiLowest': 'Lowest',
    'lab.kpiHighest': 'Dearest',
    'lab.figuresCover_one': 'Figures cover the {n} item shown, not the whole card.',
    'lab.figuresCover_other': 'Figures cover the {n} items shown, not the whole card.',
    'lab.showRetired': 'Show retired items',
    'lab.loading': 'Loading the inventory…',
    'lab.nothingMatches': 'Nothing matches',
    'lab.elsewhere_one': 'Nothing in {category} matches. There is {n} elsewhere on the card — tap All to see it.',
    'lab.elsewhere_other': 'Nothing in {category} matches. There are {n} elsewhere on the card — tap All to see them.',
    'lab.noPriceMatch': 'No item on the card falls in that price range. Items with no rate yet are never a price match.',
    'lab.addOrClear': 'Add the item, or clear the search.',
    'lab.askAdmin': 'Ask an administrator to add it.',
    'lab.testMark': '{kind} test',
    'lab.noRate': 'no rate',
    'lab.preparationLine': 'Preparation: {text}',
    'lab.bookForPatient': 'Book for patient',
    'lab.retire': 'Retire',
    'lab.ratesNote': 'Rates are maintained by an administrator. Quote from here rather than from memory, because the printed card behind the desk goes out of date first.',
    'lab.newItem': 'New inventory item',
    'lab.nameHint': 'Use the clinic’s own wording, because that is what staff say out loud.',
    'lab.rate': 'Rate',
    'lab.category': 'Category',
    'lab.sample': 'Sample',
    'lab.lab': 'Lab',
    'lab.preparation': 'Preparation',
    'lab.prepPh': '8 to 12 hours fasting. Water is allowed.',
    'lab.prepHint': 'Prints on the test slip, so leave it empty rather than guessing.',
    'lab.saveChanges': 'Save changes',
    'lab.addItem': 'Add item',
    'lab.retireTitle': 'Retire {name}?',
    'lab.retireNote': 'It stops appearing when a doctor orders tests. Prescriptions that already name it are untouched, and nothing is deleted.',
    'lab.bookTitle': 'Book {name}',
    'lab.change': 'Change',
    'lab.searchPatientPh': 'Search patient by name, mobile or code',
    'lab.addNewPatient': 'Add new patient',
    'lab.collectionTime': 'Collection time',
    'lab.collectionDate': 'Collection date',
    'lab.legendFree': 'Free',
    'lab.legendChosen': 'Chosen',
    'lab.legendBooked': 'Booked',
    'lab.legendPassed': 'Passed',
    'lab.slotBooked': '{time} — booked',
    'lab.slotPassed': '{time} — passed',
    'lab.payment': 'Payment',
    'lab.payMethod': 'Payment method',
    'lab.cashNote': 'Paid in cash at the desk. The receipt is raised there.',
    'lab.onlineNote': 'Paid now; the test is booked the moment the payment is confirmed.',
    'lab.pickPatient': 'Pick a patient',
    'lab.pickTime': 'Pick a time',
    'lab.bookAt': 'Book {time}',
    'lab.testBooked': 'Test booked',
    'lab.feePaidReceipt': '{fee} · paid, receipt {ref}',

    // ---------- account: shared ----------
    'account.clinic': 'Clinic',
    'account.account': 'Account',
    'account.appSettings': 'App settings',
    'account.changePassword': 'Change password',
    'account.staffLogins': 'Staff logins',
    'account.signOutTitle': 'Sign out?',
    'account.signOutNote': 'Anything you have typed but not saved is lost. On a shared tablet, the app lock is the lighter option between patients.',
    'account.staySignedIn': 'Stay signed in',
    'account.role.admin': 'admin',
    'account.role.platform_admin': 'platform admin',
    'account.role.doctor': 'doctor',
    'account.role.receptionist': 'receptionist',
    'account.role.lab_manager': 'lab manager',
    'account.role.patient': 'patient',

    // ---------- notifications ----------
    'account.nTitle': 'Notifications',
    'account.nCouldNotLoadMine': 'Could not load your appointments.',
    'account.nCouldNotLoad': 'Could not load: {what}.',
    'account.nWhatBookings': 'bookings',
    'account.nWhatInvestigations': 'test results',
    'account.nWhatReminders': 'reminders',
    'account.nYourUpcoming': 'Your upcoming appointments',
    'account.nBookedCount': '{n} booked',
    'account.nConfirmedPaid': 'Confirmed and paid',
    'account.nPayPending': 'Payment pending - pay from your appointments',
    'account.nRecent': 'Recent updates',
    'account.nMissedSub': 'Missed - book again if you still need to be seen',
    'account.nMissed': 'Missed',
    'account.nQueue': 'Waiting in the queue',
    'account.nWaitingCount': '{n} waiting',
    'account.nAllergy': 'Allergy: {allergies}',
    'account.nWaitingSub': '{type} · {doctor} · since {time}',
    'account.nDueToday': 'Still due today',
    'account.nResults': 'Results awaited',
    'account.nOpenCount': '{n} open',
    'account.nTestSub': '{patient} · {code} · ordered {date}',
    'account.nReminders': 'Reminders in the outbox',
    'account.nPendingCount': '{n} pending',
    'account.nReminderSub': '{mobile} · due {when}',
    'account.nMarkedSeen': 'Everything here is marked seen.',
    'account.nRefreshing': 'Refreshing…',
    'account.nMarkAll': 'Mark all seen',
    'account.nGathering': 'Gathering what needs you…',
    'account.nEmptyTitle': 'Nothing is waiting on you',
    'account.nEmptyBody': 'No one in the queue, nothing booked for the rest of today and no result outstanding.',
    'account.nLastSeen': 'Last marked seen {when}',

    // ---------- profile ----------
    'account.pBadType': 'Choose a PNG, JPEG, or WebP image.',
    'account.pTooBig': 'Profile image must be smaller than 1 MB.',
    'account.pPhotoUpdated': 'Profile photo updated.',
    'account.pPhotoFail': 'Could not update profile photo.',
    'account.pConsultingRoom': 'Consulting room',
    'account.pLabInventory': 'Lab inventory & rates',
    'account.pRecords': 'Records',
    'account.pBilling': 'Billing & invoices',
    'account.pDayEnd': 'Day-end tally',
    'account.pReports': 'Reports',
    'account.pAdminTools': 'Backups, reminders & audit',
    'account.pRoster': 'Doctors & letterheads',
    'account.pAvailability': 'Availability & holidays',
    'account.pClinicSettings': 'Clinic settings',
    'account.pTitle': 'Profile',
    'account.pChangePhoto': 'Change profile photo',
    'account.pUploading': 'Uploading photo…',
    'account.pTapPhoto': 'Tap your photo to change it',
    'account.pRegLine': '{spec} · Reg. {no}',
    'account.pPhone': 'Phone',
    'account.pOpdHours': 'OPD hours',
    'account.pMyHealth': 'My health',
    'account.pMyRecord': 'My record',
    'account.pTestsPrices': 'Tests & prices',
    'account.pClinicScreens': 'Clinic screens',
    'account.pAdministration': 'Administration',
    'account.pMyAccount': 'My account',
    'account.pHelp': 'Help centre',
    'account.pDesktop': 'Open the desktop app',

    // ---------- account ----------
    'account.aUsername': 'Username',
    'account.aRole': 'Role',
    'account.aSpecialisation': 'Specialisation',
    'account.aRegNo': 'Reg. no.',
    'account.aMissing': 'missing',
    'account.aConsultation': 'Consultation',
    'account.aAuditNote': 'Your name and role are part of every audit entry you create, so they are changed by an administrator rather than here.',
    'account.aAskAdmins': 'Ask {names}.',
    'account.aOr': ' or ',

    // ---------- change password ----------
    'account.cpMismatch': 'The two new passwords do not match.',
    'account.cpFail': 'Could not change the password.',
    'account.cpChanging': 'Changing…',
    'account.cpTemporary': 'This account is still on a temporary password. Set your own before continuing.',
    'account.cpSignedOut': 'You will be signed out of every device and will need to sign in again.',
    'account.cpCurrent': 'Current password',
    'account.cpNew': 'New password',
    'account.cpHint': 'At least 8 characters, not all digits.',
    'account.cpConfirm': 'Confirm new password',

    // ---------- app settings ----------
    'account.sThemeLight': 'Light',
    'account.sThemeDark': 'Dark',
    'account.sThemeSystem': 'Match device',
    'account.sQueue': 'Waiting queue',
    'account.sQueueSub': 'Patients who have arrived and are still waiting',
    'account.sBookings': "Today's bookings",
    'account.sBookingsSub': 'Appointments still due later today',
    'account.sResults': 'Results awaited',
    'account.sResultsSub': 'Tests ordered with no result filed yet',
    'account.sReminders': 'Pending reminders',
    'account.sRemindersSub': 'Reminders still sitting in the outbox',
    'account.sPinWrong': 'That is not the current PIN.',
    'account.sLockTurnedOff': 'App lock turned off.',
    'account.sPinFour': 'The PIN has to be exactly four digits.',
    'account.sPinMismatch': 'The two entries do not match.',
    'account.sPinChanged': 'PIN changed.',
    'account.sLockIsOn': 'App lock is on.',
    'account.sAppearance': 'Appearance',
    'account.sAppearanceNote': 'Dark suits a consulting room with the lights down. Match device follows the phone or tablet setting, including when it flips at dusk.',
    'account.sPhoneOnly': 'Applies to the phone app only',
    'account.sDesktopPalette': 'The desktop screens keep their own ink-on-paper palette.',
    'account.sLanguage': 'Language',
    'account.sLanguageNote': 'Every screen of this app, for patients and staff alike, and the tickets sent on WhatsApp use this language. Printed prescriptions, receipts and invoices stay in English.',
    'account.sNotifyNote': "This filters what the in-app feed shows on this device. It does not change what patients receive: the clinic's reminder provider is configured on the server, under Backups, reminders and audit.",
    'account.sQueueHidden': 'With the waiting queue hidden, nobody sitting outside will show up in the feed.',
    'account.sAppLock': 'App lock',
    'account.sOn': 'On',
    'account.sOff': 'Off',
    'account.sLockNote': 'A four-digit PIN for a device the desk shares. It locks this app on this device after fifteen minutes, so a tablet left on the counter between patients does not sit there showing the last record. It is not a second factor for the clinic login: the username and password still open the app on any other device.',
    'account.sLockWithPin': 'Lock this app with a PIN',
    'account.sPinSet': 'A PIN is set on this device.',
    'account.sNoPin': 'No PIN set on this device yet.',
    'account.sChangePin': 'Change the PIN',
    'account.sCurrentPin': 'Current PIN',
    'account.sNewPin': 'New PIN',
    'account.sPin': 'PIN',
    'account.sRepeat': 'Repeat it',
    'account.sPinAdvice': 'Four digits, and nothing a patient could read off a form: not the clinic phone number and not a date of birth.',
    'account.sOffNote': 'Turning the lock off clears the PIN from this device. Anyone holding the tablet then picks up wherever the last person left off.',
    'account.sTurnOff': 'Turn the lock off',
    'account.sSaveNewPin': 'Save the new PIN',
    'account.sTurnOn': 'Turn the lock on',
  },
  hi: {
    'lab.testsBooked': 'जाँचें बुक हुईं',
    'lab.add': 'जोड़ें',
    'lab.inBasket': 'जोड़ा',
    'lab.selectedOne': '1 जाँच चुनी',
    'lab.selectedN': '{n} जाँच चुनी',
    'lab.payable': '(देय)',
    'lab.chooseTime': 'सैंपल का समय चुनें',
    'lab.bookNTitle': '{n} जाँच बुक करें',
    'lab.manyCashNote': 'कई जाँचों का भुगतान काउंटर पर एक साथ होता है। UPI से भुगतान के लिए एक-एक जाँच बुक करें।',
    'lab.bookedManyFor': '{patient} के लिए {n} जाँच बुक हुईं।',
    'lab.bookedSome': '{done} बुक हुईं, फिर: {error}',
    'lab.payQr': 'QR कोड स्कैन करें',
    'lab.uncategorised': 'बिना श्रेणी',
    'lab.whenAt': '{date}, {time} बजे',
    'lab.couldNotLoad': 'इन्वेंटरी लोड नहीं हो सकी।',
    'lab.updated': '{name} अपडेट हो गया।',
    'lab.added': '{name} इन्वेंटरी में जोड़ दिया गया।',
    'lab.couldNotSave': 'सेव नहीं हो सका।',
    'lab.retired': '{name} हटा दिया गया।',
    'lab.couldNotRetire': 'इसे हटाया नहीं जा सका।',
    'lab.timePassed': 'यह समय निकल चुका है। कृपया बाद का समय चुनें।',
    'lab.bookedFor': '{patient} के लिए {test} बुक हो गया।',
    'lab.bookedPaid': '{test} बुक हो गया और भुगतान हो गया।',
    'lab.bookedPaidReceipt': '{test} बुक हो गया और भुगतान हो गया — रसीद {receipt}।',
    'lab.couldNotBook': 'टेस्ट बुक नहीं हो सका।',
    'lab.addAnItem': 'नया आइटम जोड़ें',
    'lab.title': 'लैब इन्वेंटरी',
    'lab.reload': 'फिर से लोड करें',
    'lab.searchLabel': 'रेट कार्ड में खोजें',
    'lab.searchPh': 'नाम, लैब, तैयारी या रेट — जैसे 250 या <500',
    'lab.clear': 'साफ़ करें',
    'lab.kpiItems': 'आइटम',
    'lab.kpiPriced': 'रेट वाले',
    'lab.kpiNoRate': 'बिना रेट',
    'lab.kpiMedian': 'मध्य रेट',
    'lab.kpiLowest': 'सबसे कम',
    'lab.kpiHighest': 'सबसे महँगा',
    'lab.figuresCover_one': 'ये आँकड़े दिखाए गए {n} आइटम के हैं, पूरे कार्ड के नहीं।',
    'lab.figuresCover_other': 'ये आँकड़े दिखाए गए {n} आइटम के हैं, पूरे कार्ड के नहीं।',
    'lab.showRetired': 'हटाए गए आइटम भी दिखाएँ',
    'lab.loading': 'इन्वेंटरी लोड हो रही है…',
    'lab.nothingMatches': 'कुछ नहीं मिला',
    'lab.elsewhere_one': '{category} में कुछ नहीं मिला। कार्ड में कहीं और {n} आइटम है — देखने के लिए "सभी" दबाएँ।',
    'lab.elsewhere_other': '{category} में कुछ नहीं मिला। कार्ड में कहीं और {n} आइटम हैं — देखने के लिए "सभी" दबाएँ।',
    'lab.noPriceMatch': 'इस दाम के बीच कार्ड में कोई आइटम नहीं है। जिन आइटम का रेट अभी तय नहीं है, वे दाम से नहीं खोजे जाते।',
    'lab.addOrClear': 'आइटम जोड़ें, या खोज साफ़ करें।',
    'lab.askAdmin': 'इसे जोड़ने के लिए एडमिन से कहें।',
    'lab.testMark': '{kind} टेस्ट',
    'lab.noRate': 'रेट नहीं',
    'lab.preparationLine': 'तैयारी: {text}',
    'lab.bookForPatient': 'मरीज़ के लिए बुक करें',
    'lab.retire': 'हटाएँ',
    'lab.ratesNote': 'रेट एडमिन तय करते हैं। याद से नहीं, यहीं से रेट बताएँ, क्योंकि डेस्क के पीछे छपा कार्ड सबसे पहले पुराना हो जाता है।',
    'lab.newItem': 'नया इन्वेंटरी आइटम',
    'lab.nameHint': 'क्लिनिक में जो नाम बोला जाता है, वही लिखें।',
    'lab.rate': 'रेट',
    'lab.category': 'श्रेणी',
    'lab.sample': 'सैंपल',
    'lab.lab': 'लैब',
    'lab.preparation': 'तैयारी',
    'lab.prepPh': '8 से 12 घंटे खाली पेट। पानी पी सकते हैं।',
    'lab.prepHint': 'यह टेस्ट की पर्ची पर छपता है, इसलिए पक्का न हो तो खाली छोड़ दें।',
    'lab.saveChanges': 'बदलाव सेव करें',
    'lab.addItem': 'आइटम जोड़ें',
    'lab.retireTitle': '{name} हटाएँ?',
    'lab.retireNote': 'डॉक्टर टेस्ट लिखते समय यह अब नहीं दिखेगा। जिन पर्चों में यह पहले से लिखा है, वे वैसे ही रहेंगे, और कुछ भी मिटाया नहीं जाएगा।',
    'lab.bookTitle': '{name} बुक करें',
    'lab.change': 'बदलें',
    'lab.searchPatientPh': 'नाम, मोबाइल या कोड से मरीज़ खोजें',
    'lab.addNewPatient': 'नया मरीज़ जोड़ें',
    'lab.collectionTime': 'सैंपल लेने का समय',
    'lab.collectionDate': 'सैंपल लेने की तारीख',
    'lab.legendFree': 'खाली',
    'lab.legendChosen': 'चुना गया',
    'lab.legendBooked': 'बुक',
    'lab.legendPassed': 'निकल गया',
    'lab.slotBooked': '{time} — बुक है',
    'lab.slotPassed': '{time} — निकल गया',
    'lab.payment': 'भुगतान',
    'lab.payMethod': 'भुगतान का तरीका',
    'lab.cashNote': 'डेस्क पर नकद भुगतान। रसीद वहीं बनेगी।',
    'lab.onlineNote': 'अभी भुगतान करें; भुगतान पक्का होते ही टेस्ट बुक हो जाएगा।',
    'lab.pickPatient': 'मरीज़ चुनें',
    'lab.pickTime': 'समय चुनें',
    'lab.bookAt': '{time} पर बुक करें',
    'lab.testBooked': 'टेस्ट बुक हो गया',
    'lab.feePaidReceipt': '{fee} · भुगतान हो गया, रसीद {ref}',

    'account.clinic': 'क्लिनिक',
    'account.account': 'अकाउंट',
    'account.appSettings': 'ऐप सेटिंग्स',
    'account.changePassword': 'पासवर्ड बदलें',
    'account.staffLogins': 'स्टाफ़ लॉगिन',
    'account.signOutTitle': 'साइन आउट करें?',
    'account.signOutNote': 'जो कुछ लिखा है पर सेव नहीं हुआ, वह मिट जाएगा। साझा टैबलेट पर मरीज़ों के बीच ऐप लॉक लगाना आसान रहता है।',
    'account.staySignedIn': 'साइन इन रहें',
    'account.role.admin': 'एडमिन',
    'account.role.platform_admin': 'प्लेटफ़ॉर्म एडमिन',
    'account.role.doctor': 'डॉक्टर',
    'account.role.receptionist': 'रिसेप्शनिस्ट',
    'account.role.lab_manager': 'लैब मैनेजर',
    'account.role.patient': 'मरीज़',

    'account.nTitle': 'सूचनाएँ',
    'account.nCouldNotLoadMine': 'आपकी अपॉइंटमेंट लोड नहीं हो सकीं।',
    'account.nCouldNotLoad': 'ये लोड नहीं हो सके: {what}।',
    'account.nWhatBookings': 'बुकिंग',
    'account.nWhatInvestigations': 'टेस्ट रिपोर्ट',
    'account.nWhatReminders': 'रिमाइंडर',
    'account.nYourUpcoming': 'आपकी आने वाली अपॉइंटमेंट',
    'account.nBookedCount': '{n} बुक',
    'account.nConfirmedPaid': 'पक्की और भुगतान हो गया',
    'account.nPayPending': 'भुगतान बाकी - अपनी अपॉइंटमेंट से भुगतान करें',
    'account.nRecent': 'हाल की जानकारी',
    'account.nMissedSub': 'छूट गई - अगर अब भी दिखाना है तो फिर से बुक करें',
    'account.nMissed': 'छूट गई',
    'account.nQueue': 'कतार में इंतज़ार',
    'account.nWaitingCount': '{n} इंतज़ार में',
    'account.nAllergy': 'एलर्जी: {allergies}',
    'account.nWaitingSub': '{type} · {doctor} · {time} से',
    'account.nDueToday': 'आज अभी बाकी',
    'account.nResults': 'रिपोर्ट का इंतज़ार',
    'account.nOpenCount': '{n} बाकी',
    'account.nTestSub': '{patient} · {code} · {date} को लिखा गया',
    'account.nReminders': 'भेजने के लिए बाकी रिमाइंडर',
    'account.nPendingCount': '{n} बाकी',
    'account.nReminderSub': '{mobile} · {when} को भेजना है',
    'account.nMarkedSeen': 'यहाँ सब कुछ देखा हुआ मान लिया गया।',
    'account.nRefreshing': 'रीफ़्रेश हो रहा है…',
    'account.nMarkAll': 'सब देखा हुआ करें',
    'account.nGathering': 'आपके लिए ज़रूरी चीज़ें जुटा रहे हैं…',
    'account.nEmptyTitle': 'अभी आपके लिए कुछ बाकी नहीं है',
    'account.nEmptyBody': 'कतार में कोई नहीं, आज बाकी दिन के लिए कोई बुकिंग नहीं और कोई रिपोर्ट बाकी नहीं।',
    'account.nLastSeen': 'आखिरी बार देखा हुआ किया: {when}',

    'account.pBadType': 'PNG, JPEG या WebP फ़ोटो चुनें।',
    'account.pTooBig': 'प्रोफ़ाइल फ़ोटो 1 MB से छोटी होनी चाहिए।',
    'account.pPhotoUpdated': 'प्रोफ़ाइल फ़ोटो बदल गई।',
    'account.pPhotoFail': 'प्रोफ़ाइल फ़ोटो नहीं बदल सकी।',
    'account.pConsultingRoom': 'परामर्श कक्ष',
    'account.pLabInventory': 'लैब इन्वेंटरी और रेट',
    'account.pRecords': 'रिकॉर्ड',
    'account.pBilling': 'बिलिंग और इनवॉइस',
    'account.pDayEnd': 'दिन का हिसाब',
    'account.pReports': 'रिपोर्ट',
    'account.pAdminTools': 'बैकअप, रिमाइंडर और ऑडिट',
    'account.pRoster': 'डॉक्टर और लेटरहेड',
    'account.pAvailability': 'उपलब्धता और छुट्टियाँ',
    'account.pClinicSettings': 'क्लिनिक सेटिंग्स',
    'account.pTitle': 'प्रोफ़ाइल',
    'account.pChangePhoto': 'प्रोफ़ाइल फ़ोटो बदलें',
    'account.pUploading': 'फ़ोटो अपलोड हो रही है…',
    'account.pTapPhoto': 'फ़ोटो बदलने के लिए उस पर टैप करें',
    'account.pRegLine': '{spec} · रजि. {no}',
    'account.pPhone': 'फ़ोन',
    'account.pOpdHours': 'OPD का समय',
    'account.pMyHealth': 'मेरा स्वास्थ्य',
    'account.pMyRecord': 'मेरा रिकॉर्ड',
    'account.pTestsPrices': 'टेस्ट और दाम',
    'account.pClinicScreens': 'क्लिनिक स्क्रीन',
    'account.pAdministration': 'प्रशासन',
    'account.pMyAccount': 'मेरा अकाउंट',
    'account.pHelp': 'सहायता केंद्र',
    'account.pDesktop': 'डेस्कटॉप ऐप खोलें',

    'account.aUsername': 'यूज़रनेम',
    'account.aRole': 'भूमिका',
    'account.aSpecialisation': 'विशेषज्ञता',
    'account.aRegNo': 'रजि. नं.',
    'account.aMissing': 'नहीं है',
    'account.aConsultation': 'परामर्श',
    'account.aAuditNote': 'आप जो भी ऑडिट एंट्री बनाते हैं, उसमें आपका नाम और भूमिका दर्ज होती है, इसलिए इन्हें यहाँ नहीं, एडमिन बदलते हैं।',
    'account.aAskAdmins': '{names} से कहें।',
    'account.aOr': ' या ',

    'account.cpMismatch': 'दोनों नए पासवर्ड एक जैसे नहीं हैं।',
    'account.cpFail': 'पासवर्ड नहीं बदल सका।',
    'account.cpChanging': 'बदल रहे हैं…',
    'account.cpTemporary': 'यह अकाउंट अभी अस्थायी पासवर्ड पर है। आगे बढ़ने से पहले अपना पासवर्ड बनाएँ।',
    'account.cpSignedOut': 'आप हर डिवाइस से साइन आउट हो जाएँगे और फिर से साइन इन करना होगा।',
    'account.cpCurrent': 'मौजूदा पासवर्ड',
    'account.cpNew': 'नया पासवर्ड',
    'account.cpHint': 'कम से कम 8 अक्षर, सिर्फ़ अंक नहीं।',
    'account.cpConfirm': 'नया पासवर्ड दोबारा लिखें',

    'account.sThemeLight': 'लाइट',
    'account.sThemeDark': 'डार्क',
    'account.sThemeSystem': 'डिवाइस के अनुसार',
    'account.sQueue': 'इंतज़ार की कतार',
    'account.sQueueSub': 'जो मरीज़ आ चुके हैं और अभी इंतज़ार में हैं',
    'account.sBookings': 'आज की बुकिंग',
    'account.sBookingsSub': 'आज बाद में होने वाली अपॉइंटमेंट',
    'account.sResults': 'रिपोर्ट का इंतज़ार',
    'account.sResultsSub': 'जिन टेस्ट की रिपोर्ट अभी दर्ज नहीं हुई',
    'account.sReminders': 'बाकी रिमाइंडर',
    'account.sRemindersSub': 'जो रिमाइंडर अभी भेजे नहीं गए',
    'account.sPinWrong': 'यह मौजूदा PIN नहीं है।',
    'account.sLockTurnedOff': 'ऐप लॉक बंद हो गया।',
    'account.sPinFour': 'PIN ठीक चार अंकों का होना चाहिए।',
    'account.sPinMismatch': 'दोनों PIN एक जैसे नहीं हैं।',
    'account.sPinChanged': 'PIN बदल गया।',
    'account.sLockIsOn': 'ऐप लॉक चालू है।',
    'account.sAppearance': 'रंग-रूप',
    'account.sAppearanceNote': 'कम रोशनी वाले परामर्श कक्ष के लिए डार्क अच्छा है। "डिवाइस के अनुसार" फ़ोन या टैबलेट की सेटिंग मानता है, शाम को अपने आप बदलने पर भी।',
    'account.sPhoneOnly': 'सिर्फ़ फ़ोन ऐप पर लागू',
    'account.sDesktopPalette': 'डेस्कटॉप स्क्रीन अपने ही रंगों में रहती हैं।',
    'account.sLanguage': 'भाषा',
    'account.sLanguageNote': 'इस ऐप की सभी स्क्रीन — मरीज़ों और स्टाफ़ दोनों की — और WhatsApp पर भेजे जाने वाले टिकट इसी भाषा में होंगे। छपे हुए पर्चे, रसीदें और इनवॉइस अंग्रेज़ी में ही रहेंगे।',
    'account.sNotifyNote': 'इससे सिर्फ़ इस डिवाइस पर ऐप की सूचनाएँ छँटती हैं। मरीज़ों को जो मिलता है वह नहीं बदलता: क्लिनिक का रिमाइंडर प्रोवाइडर सर्वर पर "बैकअप, रिमाइंडर और ऑडिट" में सेट होता है।',
    'account.sQueueHidden': 'इंतज़ार की कतार छिपाने पर, बाहर बैठे मरीज़ सूचनाओं में नहीं दिखेंगे।',
    'account.sAppLock': 'ऐप लॉक',
    'account.sOn': 'चालू',
    'account.sOff': 'बंद',
    'account.sLockNote': 'डेस्क पर साझा डिवाइस के लिए चार अंकों का PIN। पंद्रह मिनट बाद यह इस डिवाइस पर ऐप लॉक कर देता है, ताकि मरीज़ों के बीच काउंटर पर रखा टैबलेट पिछला रिकॉर्ड न दिखाता रहे। यह क्लिनिक लॉगिन की दूसरी सुरक्षा नहीं है: किसी और डिवाइस पर यूज़रनेम और पासवर्ड से ऐप खुल जाता है।',
    'account.sLockWithPin': 'इस ऐप को PIN से लॉक करें',
    'account.sPinSet': 'इस डिवाइस पर PIN सेट है।',
    'account.sNoPin': 'इस डिवाइस पर अभी कोई PIN सेट नहीं है।',
    'account.sChangePin': 'PIN बदलें',
    'account.sCurrentPin': 'मौजूदा PIN',
    'account.sNewPin': 'नया PIN',
    'account.sPin': 'PIN',
    'account.sRepeat': 'दोबारा लिखें',
    'account.sPinAdvice': 'चार अंक, और ऐसा कुछ नहीं जो मरीज़ किसी फ़ॉर्म से पढ़ ले: न क्लिनिक का फ़ोन नंबर, न कोई जन्मतिथि।',
    'account.sOffNote': 'लॉक बंद करने पर इस डिवाइस से PIN हट जाएगा। फिर टैबलेट उठाने वाला वहीं से शुरू करेगा जहाँ पिछला व्यक्ति छोड़ गया था।',
    'account.sTurnOff': 'लॉक बंद करें',
    'account.sSaveNewPin': 'नया PIN सेव करें',
    'account.sTurnOn': 'लॉक चालू करें',
  },
  mr: {
    'lab.testsBooked': 'तपासण्या बुक झाल्या',
    'lab.add': 'जोडा',
    'lab.inBasket': 'जोडले',
    'lab.selectedOne': '1 तपासणी निवडली',
    'lab.selectedN': '{n} तपासण्या निवडल्या',
    'lab.payable': '(देय)',
    'lab.chooseTime': 'नमुन्याची वेळ निवडा',
    'lab.bookNTitle': '{n} तपासण्या बुक करा',
    'lab.manyCashNote': 'अनेक तपासण्यांचे पैसे काउंटरवर एकत्र भरले जातात. UPI ने भरण्यासाठी एकेक तपासणी बुक करा.',
    'lab.bookedManyFor': '{patient} साठी {n} तपासण्या बुक झाल्या.',
    'lab.bookedSome': '{done} बुक झाल्या, नंतर: {error}',
    'lab.payQr': 'QR कोड स्कॅन करा',
    'lab.uncategorised': 'वर्ग नाही',
    'lab.whenAt': '{date}, {time} वाजता',
    'lab.couldNotLoad': 'इन्व्हेंटरी लोड झाली नाही.',
    'lab.updated': '{name} अपडेट झाले.',
    'lab.added': '{name} इन्व्हेंटरीत जोडले.',
    'lab.couldNotSave': 'सेव्ह झाले नाही.',
    'lab.retired': '{name} काढून टाकले.',
    'lab.couldNotRetire': 'हे काढता आले नाही.',
    'lab.timePassed': 'ही वेळ निघून गेली आहे. कृपया नंतरची वेळ निवडा.',
    'lab.bookedFor': '{patient} यांच्यासाठी {test} बुक झाली.',
    'lab.bookedPaid': '{test} बुक झाली आणि पैसे भरले.',
    'lab.bookedPaidReceipt': '{test} बुक झाली आणि पैसे भरले — पावती {receipt}.',
    'lab.couldNotBook': 'तपासणी बुक होऊ शकली नाही.',
    'lab.addAnItem': 'नवीन आयटम जोडा',
    'lab.title': 'लॅब इन्व्हेंटरी',
    'lab.reload': 'पुन्हा लोड करा',
    'lab.searchLabel': 'रेट कार्डमध्ये शोधा',
    'lab.searchPh': 'नाव, लॅब, तयारी किंवा दर — उदा. 250 किंवा <500',
    'lab.clear': 'साफ करा',
    'lab.kpiItems': 'आयटम',
    'lab.kpiPriced': 'दर असलेले',
    'lab.kpiNoRate': 'दर नाही',
    'lab.kpiMedian': 'मध्यम दर',
    'lab.kpiLowest': 'सर्वात कमी',
    'lab.kpiHighest': 'सर्वात महाग',
    'lab.figuresCover_one': 'हे आकडे दाखवलेल्या {n} आयटमचे आहेत, संपूर्ण कार्डचे नाहीत.',
    'lab.figuresCover_other': 'हे आकडे दाखवलेल्या {n} आयटमचे आहेत, संपूर्ण कार्डचे नाहीत.',
    'lab.showRetired': 'काढलेले आयटमही दाखवा',
    'lab.loading': 'इन्व्हेंटरी लोड होत आहे…',
    'lab.nothingMatches': 'काहीही जुळले नाही',
    'lab.elsewhere_one': '{category} मध्ये काहीही जुळले नाही. कार्डवर इतरत्र {n} आयटम आहे — पाहण्यासाठी "सर्व" दाबा.',
    'lab.elsewhere_other': '{category} मध्ये काहीही जुळले नाही. कार्डवर इतरत्र {n} आयटम आहेत — पाहण्यासाठी "सर्व" दाबा.',
    'lab.noPriceMatch': 'या दराच्या मर्यादेत कार्डवर कोणताही आयटम नाही. ज्या आयटमचा दर अजून ठरलेला नाही ते दराने सापडत नाहीत.',
    'lab.addOrClear': 'आयटम जोडा, किंवा शोध साफ करा.',
    'lab.askAdmin': 'हे जोडण्यासाठी ॲडमिनना सांगा.',
    'lab.testMark': '{kind} तपासणी',
    'lab.noRate': 'दर नाही',
    'lab.preparationLine': 'तयारी: {text}',
    'lab.bookForPatient': 'रुग्णासाठी बुक करा',
    'lab.retire': 'काढा',
    'lab.ratesNote': 'दर ॲडमिन ठरवतात. आठवणीतून नव्हे, इथूनच दर सांगा, कारण डेस्कमागचे छापील कार्ड सर्वात आधी जुने होते.',
    'lab.newItem': 'नवीन इन्व्हेंटरी आयटम',
    'lab.nameHint': 'क्लिनिकमध्ये जे नाव बोलले जाते तेच लिहा.',
    'lab.rate': 'दर',
    'lab.category': 'वर्ग',
    'lab.sample': 'नमुना',
    'lab.lab': 'लॅब',
    'lab.preparation': 'तयारी',
    'lab.prepPh': '8 ते 12 तास उपाशीपोटी. पाणी पिऊ शकता.',
    'lab.prepHint': 'हे तपासणीच्या चिठ्ठीवर छापले जाते, म्हणून खात्री नसेल तर रिकामे ठेवा.',
    'lab.saveChanges': 'बदल सेव्ह करा',
    'lab.addItem': 'आयटम जोडा',
    'lab.retireTitle': '{name} काढायचे?',
    'lab.retireNote': 'डॉक्टर तपासण्या लिहिताना हे आता दिसणार नाही. ज्या प्रिस्क्रिप्शनमध्ये हे आधीच आहे ती तशीच राहतील, आणि काहीही हटवले जात नाही.',
    'lab.bookTitle': '{name} बुक करा',
    'lab.change': 'बदला',
    'lab.searchPatientPh': 'नाव, मोबाइल किंवा कोडने रुग्ण शोधा',
    'lab.addNewPatient': 'नवीन रुग्ण जोडा',
    'lab.collectionTime': 'नमुना घेण्याची वेळ',
    'lab.collectionDate': 'नमुना घेण्याची तारीख',
    'lab.legendFree': 'मोकळी',
    'lab.legendChosen': 'निवडलेली',
    'lab.legendBooked': 'बुक',
    'lab.legendPassed': 'गेलेली',
    'lab.slotBooked': '{time} — बुक आहे',
    'lab.slotPassed': '{time} — वेळ गेली',
    'lab.payment': 'पेमेंट',
    'lab.payMethod': 'पेमेंटची पद्धत',
    'lab.cashNote': 'डेस्कवर रोख भरले. पावती तिथेच मिळेल.',
    'lab.onlineNote': 'आता पैसे भरा; पेमेंट निश्चित होताच तपासणी बुक होईल.',
    'lab.pickPatient': 'रुग्ण निवडा',
    'lab.pickTime': 'वेळ निवडा',
    'lab.bookAt': '{time} ला बुक करा',
    'lab.testBooked': 'तपासणी बुक झाली',
    'lab.feePaidReceipt': '{fee} · पैसे भरले, पावती {ref}',

    'account.clinic': 'क्लिनिक',
    'account.account': 'अकाउंट',
    'account.appSettings': 'ॲप सेटिंग्ज',
    'account.changePassword': 'पासवर्ड बदला',
    'account.staffLogins': 'स्टाफ लॉगिन',
    'account.signOutTitle': 'साइन आउट करायचे?',
    'account.signOutNote': 'जे लिहिले आहे पण सेव्ह केलेले नाही ते जाईल. शेअर केलेल्या टॅबलेटवर रुग्णांच्या मधल्या वेळेत ॲप लॉक हा सोपा पर्याय आहे.',
    'account.staySignedIn': 'साइन इन राहा',
    'account.role.admin': 'ॲडमिन',
    'account.role.platform_admin': 'प्लॅटफॉर्म ॲडमिन',
    'account.role.doctor': 'डॉक्टर',
    'account.role.receptionist': 'रिसेप्शनिस्ट',
    'account.role.lab_manager': 'लॅब मॅनेजर',
    'account.role.patient': 'रुग्ण',

    'account.nTitle': 'सूचना',
    'account.nCouldNotLoadMine': 'तुमच्या अपॉइंटमेंट लोड झाल्या नाहीत.',
    'account.nCouldNotLoad': 'हे लोड झाले नाही: {what}.',
    'account.nWhatBookings': 'बुकिंग',
    'account.nWhatInvestigations': 'तपासणी रिपोर्ट',
    'account.nWhatReminders': 'रिमाइंडर',
    'account.nYourUpcoming': 'तुमच्या पुढील अपॉइंटमेंट',
    'account.nBookedCount': '{n} बुक',
    'account.nConfirmedPaid': 'निश्चित आणि पैसे भरले',
    'account.nPayPending': 'पेमेंट बाकी - तुमच्या अपॉइंटमेंटमधून पैसे भरा',
    'account.nRecent': 'अलीकडील बदल',
    'account.nMissedSub': 'चुकली - अजूनही दाखवायचे असल्यास पुन्हा बुक करा',
    'account.nMissed': 'चुकली',
    'account.nQueue': 'रांगेत प्रतीक्षा',
    'account.nWaitingCount': '{n} प्रतीक्षेत',
    'account.nAllergy': 'ॲलर्जी: {allergies}',
    'account.nWaitingSub': '{type} · {doctor} · {time} पासून',
    'account.nDueToday': 'आज अजून बाकी',
    'account.nResults': 'रिपोर्टची प्रतीक्षा',
    'account.nOpenCount': '{n} बाकी',
    'account.nTestSub': '{patient} · {code} · {date} ला सांगितली',
    'account.nReminders': 'पाठवायचे बाकी रिमाइंडर',
    'account.nPendingCount': '{n} बाकी',
    'account.nReminderSub': '{mobile} · {when} ला पाठवायचे',
    'account.nMarkedSeen': 'इथले सर्व पाहिले म्हणून नोंदवले.',
    'account.nRefreshing': 'रिफ्रेश होत आहे…',
    'account.nMarkAll': 'सर्व पाहिले म्हणून नोंदवा',
    'account.nGathering': 'तुमच्यासाठी महत्त्वाचे गोळा करत आहे…',
    'account.nEmptyTitle': 'सध्या तुमच्यासाठी काहीही बाकी नाही',
    'account.nEmptyBody': 'रांगेत कोणी नाही, आजच्या उरलेल्या दिवसासाठी बुकिंग नाही आणि कोणताही रिपोर्ट बाकी नाही.',
    'account.nLastSeen': 'शेवटचे पाहिले म्हणून नोंदवले: {when}',

    'account.pBadType': 'PNG, JPEG किंवा WebP फोटो निवडा.',
    'account.pTooBig': 'प्रोफाइल फोटो 1 MB पेक्षा लहान असावा.',
    'account.pPhotoUpdated': 'प्रोफाइल फोटो बदलला.',
    'account.pPhotoFail': 'प्रोफाइल फोटो बदलता आला नाही.',
    'account.pConsultingRoom': 'तपासणी कक्ष',
    'account.pLabInventory': 'लॅब इन्व्हेंटरी आणि दर',
    'account.pRecords': 'रेकॉर्ड',
    'account.pBilling': 'बिलिंग आणि इनव्हॉइस',
    'account.pDayEnd': 'दिवसअखेरचा हिशोब',
    'account.pReports': 'अहवाल',
    'account.pAdminTools': 'बॅकअप, रिमाइंडर आणि ऑडिट',
    'account.pRoster': 'डॉक्टर आणि लेटरहेड',
    'account.pAvailability': 'उपलब्धता आणि सुट्ट्या',
    'account.pClinicSettings': 'क्लिनिक सेटिंग्ज',
    'account.pTitle': 'प्रोफाइल',
    'account.pChangePhoto': 'प्रोफाइल फोटो बदला',
    'account.pUploading': 'फोटो अपलोड होत आहे…',
    'account.pTapPhoto': 'फोटो बदलण्यासाठी त्यावर टॅप करा',
    'account.pRegLine': '{spec} · नों. {no}',
    'account.pPhone': 'फोन',
    'account.pOpdHours': 'OPD वेळ',
    'account.pMyHealth': 'माझे आरोग्य',
    'account.pMyRecord': 'माझा रेकॉर्ड',
    'account.pTestsPrices': 'तपासण्या आणि दर',
    'account.pClinicScreens': 'क्लिनिक स्क्रीन',
    'account.pAdministration': 'प्रशासन',
    'account.pMyAccount': 'माझे अकाउंट',
    'account.pHelp': 'मदत केंद्र',
    'account.pDesktop': 'डेस्कटॉप ॲप उघडा',

    'account.aUsername': 'युजरनेम',
    'account.aRole': 'भूमिका',
    'account.aSpecialisation': 'विशेषता',
    'account.aRegNo': 'नोंदणी क्र.',
    'account.aMissing': 'नाही',
    'account.aConsultation': 'तपासणी फी',
    'account.aAuditNote': 'तुम्ही केलेल्या प्रत्येक ऑडिट नोंदीत तुमचे नाव आणि भूमिका असते, म्हणून ती इथे नव्हे तर ॲडमिन बदलतात.',
    'account.aAskAdmins': '{names} यांना सांगा.',
    'account.aOr': ' किंवा ',

    'account.cpMismatch': 'दोन्ही नवीन पासवर्ड जुळत नाहीत.',
    'account.cpFail': 'पासवर्ड बदलता आला नाही.',
    'account.cpChanging': 'बदलत आहे…',
    'account.cpTemporary': 'हे अकाउंट अजून तात्पुरत्या पासवर्डवर आहे. पुढे जाण्यापूर्वी तुमचा स्वतःचा पासवर्ड ठेवा.',
    'account.cpSignedOut': 'तुम्ही सर्व डिव्हाइसवरून साइन आउट व्हाल आणि पुन्हा साइन इन करावे लागेल.',
    'account.cpCurrent': 'सध्याचा पासवर्ड',
    'account.cpNew': 'नवीन पासवर्ड',
    'account.cpHint': 'किमान 8 अक्षरे, फक्त अंक नकोत.',
    'account.cpConfirm': 'नवीन पासवर्ड पुन्हा लिहा',

    'account.sThemeLight': 'लाइट',
    'account.sThemeDark': 'डार्क',
    'account.sThemeSystem': 'डिव्हाइसप्रमाणे',
    'account.sQueue': 'प्रतीक्षा रांग',
    'account.sQueueSub': 'आलेले आणि अजून प्रतीक्षेत असलेले रुग्ण',
    'account.sBookings': 'आजच्या बुकिंग',
    'account.sBookingsSub': 'आज नंतर होणाऱ्या अपॉइंटमेंट',
    'account.sResults': 'रिपोर्टची प्रतीक्षा',
    'account.sResultsSub': 'ज्या तपासण्यांचा रिपोर्ट अजून नोंदवलेला नाही',
    'account.sReminders': 'बाकी रिमाइंडर',
    'account.sRemindersSub': 'अजून न पाठवलेले रिमाइंडर',
    'account.sPinWrong': 'हा सध्याचा PIN नाही.',
    'account.sLockTurnedOff': 'ॲप लॉक बंद झाले.',
    'account.sPinFour': 'PIN बरोबर चार अंकांचा असावा.',
    'account.sPinMismatch': 'दोन्ही PIN जुळत नाहीत.',
    'account.sPinChanged': 'PIN बदलला.',
    'account.sLockIsOn': 'ॲप लॉक चालू आहे.',
    'account.sAppearance': 'दिसणे',
    'account.sAppearanceNote': 'कमी प्रकाशातील तपासणी कक्षासाठी डार्क योग्य आहे. "डिव्हाइसप्रमाणे" फोन किंवा टॅबलेटची सेटिंग पाळते, संध्याकाळी आपोआप बदलली तरीही.',
    'account.sPhoneOnly': 'फक्त फोन ॲपला लागू',
    'account.sDesktopPalette': 'डेस्कटॉप स्क्रीन त्यांच्या स्वतःच्या रंगांतच राहतात.',
    'account.sLanguage': 'भाषा',
    'account.sLanguageNote': 'या ॲपच्या सर्व स्क्रीन — रुग्ण आणि स्टाफ दोघांच्याही — आणि WhatsApp वर पाठवलेली तिकिटे याच भाषेत असतील. छापील प्रिस्क्रिप्शन, पावत्या आणि इनव्हॉइस इंग्रजीतच राहतील.',
    'account.sNotifyNote': 'यामुळे फक्त या डिव्हाइसवरील ॲपमधील सूचना निवडल्या जातात. रुग्णांना काय मिळते ते बदलत नाही: क्लिनिकचा रिमाइंडर प्रोव्हायडर सर्व्हरवर "बॅकअप, रिमाइंडर आणि ऑडिट" मध्ये सेट केला जातो.',
    'account.sQueueHidden': 'प्रतीक्षा रांग लपवल्यास, बाहेर बसलेले रुग्ण सूचनांमध्ये दिसणार नाहीत.',
    'account.sAppLock': 'ॲप लॉक',
    'account.sOn': 'चालू',
    'account.sOff': 'बंद',
    'account.sLockNote': 'डेस्कवर शेअर केलेल्या डिव्हाइससाठी चार अंकी PIN. पंधरा मिनिटांनंतर हे या डिव्हाइसवर ॲप लॉक करते, जेणेकरून रुग्णांच्या मधल्या वेळेत काउंटरवर ठेवलेला टॅबलेट मागचा रेकॉर्ड दाखवत राहणार नाही. हा क्लिनिक लॉगिनचा दुसरा सुरक्षा टप्पा नाही: इतर कोणत्याही डिव्हाइसवर युजरनेम आणि पासवर्डने ॲप उघडते.',
    'account.sLockWithPin': 'हे ॲप PIN ने लॉक करा',
    'account.sPinSet': 'या डिव्हाइसवर PIN सेट आहे.',
    'account.sNoPin': 'या डिव्हाइसवर अजून PIN सेट नाही.',
    'account.sChangePin': 'PIN बदला',
    'account.sCurrentPin': 'सध्याचा PIN',
    'account.sNewPin': 'नवीन PIN',
    'account.sPin': 'PIN',
    'account.sRepeat': 'पुन्हा लिहा',
    'account.sPinAdvice': 'चार अंक, आणि रुग्णाला एखाद्या फॉर्मवरून वाचता येईल असे काही नको: क्लिनिकचा फोन नंबर नको आणि जन्मतारीख नको.',
    'account.sOffNote': 'लॉक बंद केल्यावर या डिव्हाइसवरून PIN काढला जातो. मग टॅबलेट हातात घेणारा मागच्या व्यक्तीने जिथे सोडले तिथूनच पुढे सुरू करतो.',
    'account.sTurnOff': 'लॉक बंद करा',
    'account.sSaveNewPin': 'नवीन PIN सेव्ह करा',
    'account.sTurnOn': 'लॉक चालू करा',
  },
  or: {
    'lab.testsBooked': 'ପରୀକ୍ଷା ବୁକ୍ ହେଲା',
    'lab.add': 'ଯୋଡ଼ନ୍ତୁ',
    'lab.inBasket': 'ଯୋଡ଼ାଗଲା',
    'lab.selectedOne': '1ଟି ପରୀକ୍ଷା ବଛାଗଲା',
    'lab.selectedN': '{n}ଟି ପରୀକ୍ଷା ବଛାଗଲା',
    'lab.payable': '(ଦେୟ)',
    'lab.chooseTime': 'ନମୁନା ସମୟ ବାଛନ୍ତୁ',
    'lab.bookNTitle': '{n}ଟି ପରୀକ୍ଷା ବୁକ୍',
    'lab.manyCashNote': 'ଅନେକ ପରୀକ୍ଷାର ଦେୟ କାଉଣ୍ଟରରେ ଏକାଠି ହୁଏ। UPI ରେ ଦେବା ପାଇଁ ଗୋଟିଏ ଗୋଟିଏ ପରୀକ୍ଷା ବୁକ୍ କରନ୍ତୁ।',
    'lab.bookedManyFor': '{patient} ଙ୍କ ପାଇଁ {n}ଟି ପରୀକ୍ଷା ବୁକ୍ ହେଲା।',
    'lab.bookedSome': '{done} ବୁକ୍ ହେଲା, ତାପରେ: {error}',
    'lab.payQr': 'QR କୋଡ୍ ସ୍କାନ୍ କରନ୍ତୁ',
    'lab.uncategorised': 'ବର୍ଗ ନାହିଁ',
    'lab.whenAt': '{date}, {time} ରେ',
    'lab.couldNotLoad': 'ଇନଭେଣ୍ଟୋରି ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'lab.updated': '{name} ଅପଡେଟ୍ ହେଲା।',
    'lab.added': '{name} ଇନଭେଣ୍ଟୋରିରେ ଯୋଡାଗଲା।',
    'lab.couldNotSave': 'ସେଭ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'lab.retired': '{name} ହଟାଗଲା।',
    'lab.couldNotRetire': 'ଏହାକୁ ହଟାଯାଇପାରିଲା ନାହିଁ।',
    'lab.timePassed': 'ଏହି ସମୟ ଚାଲିଗଲାଣି। ଦୟାକରି ପରର ସମୟ ବାଛନ୍ତୁ।',
    'lab.bookedFor': '{patient} ଙ୍କ ପାଇଁ {test} ବୁକ୍ ହେଲା।',
    'lab.bookedPaid': '{test} ବୁକ୍ ହେଲା ଓ ଦେୟ ଦିଆଗଲା।',
    'lab.bookedPaidReceipt': '{test} ବୁକ୍ ହେଲା ଓ ଦେୟ ଦିଆଗଲା — ରସିଦ {receipt}।',
    'lab.couldNotBook': 'ପରୀକ୍ଷା ବୁକ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'lab.addAnItem': 'ନୂଆ ଆଇଟମ୍ ଯୋଡନ୍ତୁ',
    'lab.title': 'ଲ୍ୟାବ୍ ଇନଭେଣ୍ଟୋରି',
    'lab.reload': 'ପୁଣି ଲୋଡ୍ କରନ୍ତୁ',
    'lab.searchLabel': 'ରେଟ୍ କାର୍ଡରେ ଖୋଜନ୍ତୁ',
    'lab.searchPh': 'ନାମ, ଲ୍ୟାବ୍, ପ୍ରସ୍ତୁତି ବା ଦର — ଯେପରି 250 ବା <500',
    'lab.clear': 'ସଫା କରନ୍ତୁ',
    'lab.kpiItems': 'ଆଇଟମ୍',
    'lab.kpiPriced': 'ଦର ଥିବା',
    'lab.kpiNoRate': 'ଦର ନାହିଁ',
    'lab.kpiMedian': 'ମଧ୍ୟମ ଦର',
    'lab.kpiLowest': 'ସବୁଠୁ କମ୍',
    'lab.kpiHighest': 'ସବୁଠୁ ମହଙ୍ଗା',
    'lab.figuresCover_one': 'ଏହି ସଂଖ୍ୟା ଦେଖାଯାଇଥିବା {n}ଟି ଆଇଟମ୍‌ର, ସମ୍ପୂର୍ଣ୍ଣ କାର୍ଡର ନୁହେଁ।',
    'lab.figuresCover_other': 'ଏହି ସଂଖ୍ୟା ଦେଖାଯାଇଥିବା {n}ଟି ଆଇଟମ୍‌ର, ସମ୍ପୂର୍ଣ୍ଣ କାର୍ଡର ନୁହେଁ।',
    'lab.showRetired': 'ହଟାଯାଇଥିବା ଆଇଟମ୍ ମଧ୍ୟ ଦେଖାନ୍ତୁ',
    'lab.loading': 'ଇନଭେଣ୍ଟୋରି ଲୋଡ୍ ହେଉଛି…',
    'lab.nothingMatches': 'କିଛି ମିଳିଲା ନାହିଁ',
    'lab.elsewhere_one': '{category} ରେ କିଛି ମିଳିଲା ନାହିଁ। କାର୍ଡର ଅନ୍ୟତ୍ର {n}ଟି ଆଇଟମ୍ ଅଛି — ଦେଖିବା ପାଇଁ "ସବୁ" ଦବାନ୍ତୁ।',
    'lab.elsewhere_other': '{category} ରେ କିଛି ମିଳିଲା ନାହିଁ। କାର୍ଡର ଅନ୍ୟତ୍ର {n}ଟି ଆଇଟମ୍ ଅଛି — ଦେଖିବା ପାଇଁ "ସବୁ" ଦବାନ୍ତୁ।',
    'lab.noPriceMatch': 'ଏହି ଦର ସୀମା ମଧ୍ୟରେ କାର୍ଡରେ କୌଣସି ଆଇଟମ୍ ନାହିଁ। ଯେଉଁ ଆଇଟମ୍‌ର ଦର ଏଯାଏଁ ସ୍ଥିର ହୋଇନାହିଁ, ତାହା ଦର ଦ୍ୱାରା ମିଳେ ନାହିଁ।',
    'lab.addOrClear': 'ଆଇଟମ୍ ଯୋଡନ୍ତୁ, କିମ୍ବା ଖୋଜା ସଫା କରନ୍ତୁ।',
    'lab.askAdmin': 'ଏହା ଯୋଡିବା ପାଇଁ ଆଡମିନ୍‌ଙ୍କୁ କୁହନ୍ତୁ।',
    'lab.testMark': '{kind} ପରୀକ୍ଷା',
    'lab.noRate': 'ଦର ନାହିଁ',
    'lab.preparationLine': 'ପ୍ରସ୍ତୁତି: {text}',
    'lab.bookForPatient': 'ରୋଗୀଙ୍କ ପାଇଁ ବୁକ୍ କରନ୍ତୁ',
    'lab.retire': 'ହଟାନ୍ତୁ',
    'lab.ratesNote': 'ଦର ଆଡମିନ୍ ସ୍ଥିର କରନ୍ତି। ମନେ ପକାଇ ନୁହେଁ, ଏଠାରୁ ଦର କୁହନ୍ତୁ, କାରଣ ଡେସ୍କ ପଛର ଛପା କାର୍ଡ ସବୁଠୁ ଆଗ ପୁରୁଣା ହୋଇଯାଏ।',
    'lab.newItem': 'ନୂଆ ଇନଭେଣ୍ଟୋରି ଆଇଟମ୍',
    'lab.nameHint': 'କ୍ଲିନିକ୍‌ରେ ଯେଉଁ ନାମ କୁହାଯାଏ, ସେହି ନାମ ଲେଖନ୍ତୁ।',
    'lab.rate': 'ଦର',
    'lab.category': 'ବର୍ଗ',
    'lab.sample': 'ନମୁନା',
    'lab.lab': 'ଲ୍ୟାବ୍',
    'lab.preparation': 'ପ୍ରସ୍ତୁତି',
    'lab.prepPh': '8 ରୁ 12 ଘଣ୍ଟା ଖାଲି ପେଟ। ପାଣି ପିଇପାରିବେ।',
    'lab.prepHint': 'ଏହା ପରୀକ୍ଷା ସ୍ଲିପ୍‌ରେ ଛପାହୁଏ, ତେଣୁ ନିଶ୍ଚିତ ନ ହେଲେ ଖାଲି ଛାଡନ୍ତୁ।',
    'lab.saveChanges': 'ପରିବର୍ତ୍ତନ ସେଭ୍ କରନ୍ତୁ',
    'lab.addItem': 'ଆଇଟମ୍ ଯୋଡନ୍ତୁ',
    'lab.retireTitle': '{name} ହଟାଇବେ?',
    'lab.retireNote': 'ଡାକ୍ତର ପରୀକ୍ଷା ଲେଖିବା ବେଳେ ଏହା ଆଉ ଦେଖାଯିବ ନାହିଁ। ଯେଉଁ ପ୍ରେସକ୍ରିପସନ୍‌ରେ ଏହା ପୂର୍ବରୁ ଅଛି, ତାହା ଯେମିତି ଅଛି ସେମିତି ରହିବ, ଏବଂ କିଛି ଡିଲିଟ୍ ହୁଏ ନାହିଁ।',
    'lab.bookTitle': '{name} ବୁକ୍ କରନ୍ତୁ',
    'lab.change': 'ବଦଳାନ୍ତୁ',
    'lab.searchPatientPh': 'ନାମ, ମୋବାଇଲ୍ ବା କୋଡ୍ ଦ୍ୱାରା ରୋଗୀ ଖୋଜନ୍ତୁ',
    'lab.addNewPatient': 'ନୂଆ ରୋଗୀ ଯୋଡନ୍ତୁ',
    'lab.collectionTime': 'ନମୁନା ନେବା ସମୟ',
    'lab.collectionDate': 'ନମୁନା ନେବା ତାରିଖ',
    'lab.legendFree': 'ଖାଲି',
    'lab.legendChosen': 'ବଛାଯାଇଛି',
    'lab.legendBooked': 'ବୁକ୍ ହୋଇଛି',
    'lab.legendPassed': 'ଚାଲିଗଲା',
    'lab.slotBooked': '{time} — ବୁକ୍ ହୋଇଛି',
    'lab.slotPassed': '{time} — ସମୟ ଚାଲିଗଲା',
    'lab.payment': 'ଦେୟ',
    'lab.payMethod': 'ଦେୟ ପଦ୍ଧତି',
    'lab.cashNote': 'ଡେସ୍କରେ ନଗଦ ଦେୟ। ରସିଦ ସେଠାରେ ମିଳିବ।',
    'lab.onlineNote': 'ଏବେ ଦେୟ ଦିଅନ୍ତୁ; ଦେୟ ନିଶ୍ଚିତ ହେବା କ୍ଷଣି ପରୀକ୍ଷା ବୁକ୍ ହୋଇଯିବ।',
    'lab.pickPatient': 'ରୋଗୀ ବାଛନ୍ତୁ',
    'lab.pickTime': 'ସମୟ ବାଛନ୍ତୁ',
    'lab.bookAt': '{time} ରେ ବୁକ୍ କରନ୍ତୁ',
    'lab.testBooked': 'ପରୀକ୍ଷା ବୁକ୍ ହେଲା',
    'lab.feePaidReceipt': '{fee} · ଦେୟ ଦିଆଗଲା, ରସିଦ {ref}',

    'account.clinic': 'କ୍ଲିନିକ୍',
    'account.account': 'ଆକାଉଣ୍ଟ',
    'account.appSettings': 'ଆପ୍ ସେଟିଂସ୍',
    'account.changePassword': 'ପାସୱାର୍ଡ ବଦଳାନ୍ତୁ',
    'account.staffLogins': 'ଷ୍ଟାଫ୍ ଲଗଇନ୍',
    'account.signOutTitle': 'ସାଇନ୍ ଆଉଟ୍ କରିବେ?',
    'account.signOutNote': 'ଯାହା ଲେଖିଛନ୍ତି କିନ୍ତୁ ସେଭ୍ କରିନାହାନ୍ତି, ତାହା ହଜିଯିବ। ସେୟାର୍ ହେଉଥିବା ଟାବଲେଟ୍‌ରେ ରୋଗୀଙ୍କ ମଝିରେ ଆପ୍ ଲକ୍ ସହଜ ବିକଳ୍ପ।',
    'account.staySignedIn': 'ସାଇନ୍ ଇନ୍ ରୁହନ୍ତୁ',
    'account.role.admin': 'ଆଡମିନ୍',
    'account.role.platform_admin': 'ପ୍ଲାଟଫର୍ମ ଆଡମିନ୍',
    'account.role.doctor': 'ଡାକ୍ତର',
    'account.role.receptionist': 'ରିସେପସନିଷ୍ଟ',
    'account.role.lab_manager': 'ଲ୍ୟାବ୍ ମ୍ୟାନେଜର୍',
    'account.role.patient': 'ରୋଗୀ',

    'account.nTitle': 'ସୂଚନା',
    'account.nCouldNotLoadMine': 'ଆପଣଙ୍କ ଆପଏଣ୍ଟମେଣ୍ଟ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'account.nCouldNotLoad': 'ଏଗୁଡିକ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ: {what}।',
    'account.nWhatBookings': 'ବୁକିଂ',
    'account.nWhatInvestigations': 'ପରୀକ୍ଷା ରିପୋର୍ଟ',
    'account.nWhatReminders': 'ରିମାଇଣ୍ଡର୍',
    'account.nYourUpcoming': 'ଆପଣଙ୍କ ଆଗାମୀ ଆପଏଣ୍ଟମେଣ୍ଟ',
    'account.nBookedCount': '{n} ବୁକ୍',
    'account.nConfirmedPaid': 'ନିଶ୍ଚିତ ଓ ଦେୟ ଦିଆଗଲା',
    'account.nPayPending': 'ଦେୟ ବାକି - ଆପଣଙ୍କ ଆପଏଣ୍ଟମେଣ୍ଟରୁ ଦେୟ ଦିଅନ୍ତୁ',
    'account.nRecent': 'ସାମ୍ପ୍ରତିକ ଅପଡେଟ୍',
    'account.nMissedSub': 'ଛୁଟିଗଲା - ଏବେ ବି ଦେଖାଇବାକୁ ଥିଲେ ପୁଣି ବୁକ୍ କରନ୍ତୁ',
    'account.nMissed': 'ଛୁଟିଗଲା',
    'account.nQueue': 'ଧାଡିରେ ଅପେକ୍ଷା',
    'account.nWaitingCount': '{n} ଅପେକ୍ଷାରେ',
    'account.nAllergy': 'ଆଲର୍ଜି: {allergies}',
    'account.nWaitingSub': '{type} · {doctor} · {time} ଠାରୁ',
    'account.nDueToday': 'ଆଜି ଏବେ ବି ବାକି',
    'account.nResults': 'ରିପୋର୍ଟ ଅପେକ୍ଷାରେ',
    'account.nOpenCount': '{n} ବାକି',
    'account.nTestSub': '{patient} · {code} · {date} ରେ ଲେଖାଗଲା',
    'account.nReminders': 'ପଠାଇବାକୁ ବାକି ରିମାଇଣ୍ଡର୍',
    'account.nPendingCount': '{n} ବାକି',
    'account.nReminderSub': '{mobile} · {when} ରେ ପଠାଇବାକୁ',
    'account.nMarkedSeen': 'ଏଠାରେ ସବୁକିଛି ଦେଖାଗଲା ବୋଲି ଚିହ୍ନିତ ହେଲା।',
    'account.nRefreshing': 'ରିଫ୍ରେଶ୍ ହେଉଛି…',
    'account.nMarkAll': 'ସବୁ ଦେଖାଗଲା ବୋଲି ଚିହ୍ନିତ କରନ୍ତୁ',
    'account.nGathering': 'ଆପଣଙ୍କ ପାଇଁ ଦରକାରୀ ବିଷୟ ଏକାଠି କରୁଛି…',
    'account.nEmptyTitle': 'ଏବେ ଆପଣଙ୍କ ପାଇଁ କିଛି ବାକି ନାହିଁ',
    'account.nEmptyBody': 'ଧାଡିରେ କେହି ନାହାନ୍ତି, ଆଜି ବାକି ଦିନ ପାଇଁ କୌଣସି ବୁକିଂ ନାହିଁ ଏବଂ କୌଣସି ରିପୋର୍ଟ ବାକି ନାହିଁ।',
    'account.nLastSeen': 'ଶେଷ ଥର ଦେଖାଗଲା ବୋଲି ଚିହ୍ନିତ: {when}',

    'account.pBadType': 'PNG, JPEG ବା WebP ଫଟୋ ବାଛନ୍ତୁ।',
    'account.pTooBig': 'ପ୍ରୋଫାଇଲ୍ ଫଟୋ 1 MB ରୁ ଛୋଟ ହେବା ଦରକାର।',
    'account.pPhotoUpdated': 'ପ୍ରୋଫାଇଲ୍ ଫଟୋ ବଦଳିଗଲା।',
    'account.pPhotoFail': 'ପ୍ରୋଫାଇଲ୍ ଫଟୋ ବଦଳାଯାଇପାରିଲା ନାହିଁ।',
    'account.pConsultingRoom': 'ପରାମର୍ଶ କକ୍ଷ',
    'account.pLabInventory': 'ଲ୍ୟାବ୍ ଇନଭେଣ୍ଟୋରି ଓ ଦର',
    'account.pRecords': 'ରେକର୍ଡ',
    'account.pBilling': 'ବିଲିଂ ଓ ଇନଭଏସ୍',
    'account.pDayEnd': 'ଦିନ ଶେଷ ହିସାବ',
    'account.pReports': 'ରିପୋର୍ଟ',
    'account.pAdminTools': 'ବ୍ୟାକଅପ୍, ରିମାଇଣ୍ଡର୍ ଓ ଅଡିଟ୍',
    'account.pRoster': 'ଡାକ୍ତର ଓ ଲେଟରହେଡ୍',
    'account.pAvailability': 'ଉପଲବ୍ଧତା ଓ ଛୁଟି',
    'account.pClinicSettings': 'କ୍ଲିନିକ୍ ସେଟିଂସ୍',
    'account.pTitle': 'ପ୍ରୋଫାଇଲ୍',
    'account.pChangePhoto': 'ପ୍ରୋଫାଇଲ୍ ଫଟୋ ବଦଳାନ୍ତୁ',
    'account.pUploading': 'ଫଟୋ ଅପଲୋଡ୍ ହେଉଛି…',
    'account.pTapPhoto': 'ଫଟୋ ବଦଳାଇବାକୁ ତାହା ଉପରେ ଟାପ୍ କରନ୍ତୁ',
    'account.pRegLine': '{spec} · ରେଜି. {no}',
    'account.pPhone': 'ଫୋନ୍',
    'account.pOpdHours': 'OPD ସମୟ',
    'account.pMyHealth': 'ମୋ ସ୍ୱାସ୍ଥ୍ୟ',
    'account.pMyRecord': 'ମୋ ରେକର୍ଡ',
    'account.pTestsPrices': 'ପରୀକ୍ଷା ଓ ଦର',
    'account.pClinicScreens': 'କ୍ଲିନିକ୍ ସ୍କ୍ରିନ୍',
    'account.pAdministration': 'ପ୍ରଶାସନ',
    'account.pMyAccount': 'ମୋ ଆକାଉଣ୍ଟ',
    'account.pHelp': 'ସହାୟତା କେନ୍ଦ୍ର',
    'account.pDesktop': 'ଡେସ୍କଟପ୍ ଆପ୍ ଖୋଲନ୍ତୁ',

    'account.aUsername': 'ୟୁଜରନେମ୍',
    'account.aRole': 'ଭୂମିକା',
    'account.aSpecialisation': 'ବିଶେଷଜ୍ଞତା',
    'account.aRegNo': 'ରେଜି. ନଂ.',
    'account.aMissing': 'ନାହିଁ',
    'account.aConsultation': 'ପରାମର୍ଶ',
    'account.aAuditNote': 'ଆପଣ ତିଆରି କରୁଥିବା ପ୍ରତ୍ୟେକ ଅଡିଟ୍ ଏଣ୍ଟ୍ରିରେ ଆପଣଙ୍କ ନାମ ଓ ଭୂମିକା ରହେ, ତେଣୁ ଏଗୁଡିକ ଏଠାରେ ନୁହେଁ, ଆଡମିନ୍ ବଦଳାନ୍ତି।',
    'account.aAskAdmins': '{names} ଙ୍କୁ କୁହନ୍ତୁ।',
    'account.aOr': ' କିମ୍ବା ',

    'account.cpMismatch': 'ଦୁଇଟି ନୂଆ ପାସୱାର୍ଡ ମେଳ ଖାଉନାହିଁ।',
    'account.cpFail': 'ପାସୱାର୍ଡ ବଦଳାଯାଇପାରିଲା ନାହିଁ।',
    'account.cpChanging': 'ବଦଳାଉଛି…',
    'account.cpTemporary': 'ଏହି ଆକାଉଣ୍ଟ ଏବେ ବି ଅସ୍ଥାୟୀ ପାସୱାର୍ଡରେ ଅଛି। ଆଗକୁ ବଢିବା ପୂର୍ବରୁ ନିଜର ପାସୱାର୍ଡ ସେଟ୍ କରନ୍ତୁ।',
    'account.cpSignedOut': 'ଆପଣ ସବୁ ଡିଭାଇସ୍‌ରୁ ସାଇନ୍ ଆଉଟ୍ ହୋଇଯିବେ ଏବଂ ପୁଣି ସାଇନ୍ ଇନ୍ କରିବାକୁ ପଡିବ।',
    'account.cpCurrent': 'ବର୍ତ୍ତମାନର ପାସୱାର୍ଡ',
    'account.cpNew': 'ନୂଆ ପାସୱାର୍ଡ',
    'account.cpHint': 'ଅତି କମ୍‌ରେ 8 ଅକ୍ଷର, କେବଳ ଅଙ୍କ ନୁହେଁ।',
    'account.cpConfirm': 'ନୂଆ ପାସୱାର୍ଡ ପୁଣି ଲେଖନ୍ତୁ',

    'account.sThemeLight': 'ଲାଇଟ୍',
    'account.sThemeDark': 'ଡାର୍କ',
    'account.sThemeSystem': 'ଡିଭାଇସ୍ ଅନୁସାରେ',
    'account.sQueue': 'ଅପେକ୍ଷା ଧାଡି',
    'account.sQueueSub': 'ଯେଉଁ ରୋଗୀ ଆସିସାରିଛନ୍ତି ଓ ଏବେ ବି ଅପେକ୍ଷା କରୁଛନ୍ତି',
    'account.sBookings': 'ଆଜିର ବୁକିଂ',
    'account.sBookingsSub': 'ଆଜି ପରେ ହେବାକୁ ଥିବା ଆପଏଣ୍ଟମେଣ୍ଟ',
    'account.sResults': 'ରିପୋର୍ଟ ଅପେକ୍ଷାରେ',
    'account.sResultsSub': 'ଯେଉଁ ପରୀକ୍ଷାର ରିପୋର୍ଟ ଏଯାଏଁ ଦାଖଲ ହୋଇନାହିଁ',
    'account.sReminders': 'ବାକି ରିମାଇଣ୍ଡର୍',
    'account.sRemindersSub': 'ଏଯାଏଁ ପଠାଯାଇନଥିବା ରିମାଇଣ୍ଡର୍',
    'account.sPinWrong': 'ଏହା ବର୍ତ୍ତମାନର PIN ନୁହେଁ।',
    'account.sLockTurnedOff': 'ଆପ୍ ଲକ୍ ବନ୍ଦ ହେଲା।',
    'account.sPinFour': 'PIN ଠିକ୍ ଚାରି ଅଙ୍କର ହେବା ଦରକାର।',
    'account.sPinMismatch': 'ଦୁଇଟି PIN ମେଳ ଖାଉନାହିଁ।',
    'account.sPinChanged': 'PIN ବଦଳିଗଲା।',
    'account.sLockIsOn': 'ଆପ୍ ଲକ୍ ଚାଲୁ ଅଛି।',
    'account.sAppearance': 'ରୂପ',
    'account.sAppearanceNote': 'କମ୍ ଆଲୋକ ଥିବା ପରାମର୍ଶ କକ୍ଷ ପାଇଁ ଡାର୍କ ଭଲ। "ଡିଭାଇସ୍ ଅନୁସାରେ" ଫୋନ୍ ବା ଟାବଲେଟ୍‌ର ସେଟିଂ ମାନେ, ସନ୍ଧ୍ୟାରେ ନିଜେ ବଦଳିଲେ ମଧ୍ୟ।',
    'account.sPhoneOnly': 'କେବଳ ଫୋନ୍ ଆପ୍‌ରେ ଲାଗୁ',
    'account.sDesktopPalette': 'ଡେସ୍କଟପ୍ ସ୍କ୍ରିନ୍ ନିଜ ରଙ୍ଗରେ ହିଁ ରହେ।',
    'account.sLanguage': 'ଭାଷା',
    'account.sLanguageNote': 'ଏହି ଆପ୍‌ର ସବୁ ସ୍କ୍ରିନ୍ — ରୋଗୀ ଓ ଷ୍ଟାଫ୍ ଉଭୟଙ୍କର — ଏବଂ WhatsApp ରେ ପଠାଯାଉଥିବା ଟିକେଟ୍ ଏହି ଭାଷାରେ ରହିବ। ଛପା ପ୍ରେସକ୍ରିପସନ୍, ରସିଦ ଓ ଇନଭଏସ୍ ଇଂରାଜୀରେ ହିଁ ରହିବ।',
    'account.sNotifyNote': 'ଏହା କେବଳ ଏହି ଡିଭାଇସ୍‌ରେ ଆପ୍‌ର ସୂଚନା ବାଛେ। ରୋଗୀମାନେ ଯାହା ପାଆନ୍ତି ତାହା ବଦଳେ ନାହିଁ: କ୍ଲିନିକ୍‌ର ରିମାଇଣ୍ଡର୍ ପ୍ରୋଭାଇଡର୍ ସର୍ଭରରେ "ବ୍ୟାକଅପ୍, ରିମାଇଣ୍ଡର୍ ଓ ଅଡିଟ୍" ରେ ସେଟ୍ ହୁଏ।',
    'account.sQueueHidden': 'ଅପେକ୍ଷା ଧାଡି ଲୁଚାଇଲେ, ବାହାରେ ବସିଥିବା କେହି ସୂଚନାରେ ଦେଖାଯିବେ ନାହିଁ।',
    'account.sAppLock': 'ଆପ୍ ଲକ୍',
    'account.sOn': 'ଚାଲୁ',
    'account.sOff': 'ବନ୍ଦ',
    'account.sLockNote': 'ଡେସ୍କରେ ସେୟାର୍ ହେଉଥିବା ଡିଭାଇସ୍ ପାଇଁ ଚାରି ଅଙ୍କର PIN। ପନ୍ଦର ମିନିଟ୍ ପରେ ଏହା ଏହି ଡିଭାଇସ୍‌ରେ ଆପ୍ ଲକ୍ କରେ, ଯାହାଫଳରେ ରୋଗୀଙ୍କ ମଝିରେ କାଉଣ୍ଟରରେ ରଖାଯାଇଥିବା ଟାବଲେଟ୍ ଶେଷ ରେକର୍ଡ ଦେଖାଉ ନ ଥାଏ। ଏହା କ୍ଲିନିକ୍ ଲଗଇନ୍‌ର ଦ୍ୱିତୀୟ ସୁରକ୍ଷା ନୁହେଁ: ଅନ୍ୟ କୌଣସି ଡିଭାଇସ୍‌ରେ ୟୁଜରନେମ୍ ଓ ପାସୱାର୍ଡରେ ଆପ୍ ଖୋଲିଯାଏ।',
    'account.sLockWithPin': 'ଏହି ଆପ୍‌କୁ PIN ରେ ଲକ୍ କରନ୍ତୁ',
    'account.sPinSet': 'ଏହି ଡିଭାଇସ୍‌ରେ PIN ସେଟ୍ ଅଛି।',
    'account.sNoPin': 'ଏହି ଡିଭାଇସ୍‌ରେ ଏଯାଏଁ PIN ସେଟ୍ ହୋଇନାହିଁ।',
    'account.sChangePin': 'PIN ବଦଳାନ୍ତୁ',
    'account.sCurrentPin': 'ବର୍ତ୍ତମାନର PIN',
    'account.sNewPin': 'ନୂଆ PIN',
    'account.sPin': 'PIN',
    'account.sRepeat': 'ପୁଣି ଲେଖନ୍ତୁ',
    'account.sPinAdvice': 'ଚାରି ଅଙ୍କ, ଏବଂ ରୋଗୀ କୌଣସି ଫର୍ମରୁ ପଢିପାରିବେ ଏମିତି କିଛି ନୁହେଁ: କ୍ଲିନିକ୍‌ର ଫୋନ୍ ନମ୍ବର ନୁହେଁ କି ଜନ୍ମ ତାରିଖ ନୁହେଁ।',
    'account.sOffNote': 'ଲକ୍ ବନ୍ଦ କଲେ ଏହି ଡିଭାଇସ୍‌ରୁ PIN ହଟିଯିବ। ତା’ପରେ ଟାବଲେଟ୍ ଧରିଥିବା ବ୍ୟକ୍ତି ଶେଷ ବ୍ୟକ୍ତି ଯେଉଁଠି ଛାଡିଥିଲେ ସେଠାରୁ ଆରମ୍ଭ କରିବେ।',
    'account.sTurnOff': 'ଲକ୍ ବନ୍ଦ କରନ୍ତୁ',
    'account.sSaveNewPin': 'ନୂଆ PIN ସେଭ୍ କରନ୍ତୁ',
    'account.sTurnOn': 'ଲକ୍ ଚାଲୁ କରନ୍ତୁ',
  },
})
